import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import './StudentClasses.css';
import { Card, CardContent, Typography, Chip, CircularProgress, Alert } from '@mui/material';
import SchoolIcon from '@mui/icons-material/School';
import PersonIcon from '@mui/icons-material/Person';
import RoomIcon from '@mui/icons-material/Room';
import axios from 'axios';
import { API_BASE_URL } from '../utils/api';

interface Class {
  id: number;
  name: string;
  subject: string;
  grade_level: string;
  description: string;
  room_number: string;
  period: string;
  days_of_week: string;
  academic_year: string;
  semester: string;
  teacher_name: string;
  enrolled_date: string;
}

const StudentClasses: React.FC = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [classes, setClasses] = useState<Class[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    fetchClasses();
  }, []);

  const fetchClasses = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('auth_token');
      const response = await axios.get(`${API_BASE_URL}/api/student/classes`, {
        headers: {
          Authorization: `Bearer ${token}`
        }
      });
      setClasses(response.data.classes);
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to load classes');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="student-classes-loading">
        <CircularProgress />
        <p>{t('studentClasses.description')}</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="student-classes-container">
        <Alert severity="error">{error}</Alert>
      </div>
    );
  }

  return (
    <div className="student-classes-container">
      <div className="student-classes-header">
        <SchoolIcon sx={{ fontSize: 40, marginRight: 2 }} />
        <div>
          <h1>{t('studentClasses.title')}</h1>
          <p>{t('studentClasses.description')}</p>
        </div>
      </div>

      {classes.length === 0 ? (
        <div className="no-classes">
          <SchoolIcon sx={{ fontSize: 80, color: '#ccc' }} />
          <Typography variant="h6" color="text.secondary">
            {t('studentClasses.noClasses')}
          </Typography>
        </div>
      ) : (
        <div className="classes-grid">
          {classes.map((classItem) => (
            <Card 
              key={classItem.id} 
              className="class-card clickable-card"
              onClick={() => navigate(`/student/classes/${classItem.id}/feed`)}
              sx={{ cursor: 'pointer', '&:hover': { boxShadow: 6, transform: 'translateY(-2px)', transition: 'all 0.2s' } }}
            >
              <CardContent>
                <div className="class-header">
                  <Typography variant="h5" component="h2" gutterBottom>
                    {classItem.name}
                  </Typography>
                  {classItem.subject && (
                    <Chip label={classItem.subject} color="primary" size="small" />
                  )}
                </div>

                {classItem.description && (
                  <Typography variant="body2" color="text.secondary" paragraph>
                    {classItem.description}
                  </Typography>
                )}

                <div className="class-details">
                  {classItem.teacher_name && (
                    <div className="detail-item">
                      <PersonIcon fontSize="small" />
                      <span><strong>{t('studentClasses.teacher')}</strong> {classItem.teacher_name}</span>
                    </div>
                  )}

                  {classItem.room_number && (
                    <div className="detail-item">
                      <RoomIcon fontSize="small" />
                      <span><strong>{t('studentClasses.room')}</strong> {classItem.room_number}</span>
                    </div>
                  )}

                  {classItem.period && (
                    <div className="detail-item">
                      <span><strong>{t('studentClasses.period')}</strong> {classItem.period}</span>
                    </div>
                  )}

                  {classItem.days_of_week && (
                    <div className="detail-item">
                      <span><strong>{t('studentClasses.days')}</strong> {classItem.days_of_week}</span>
                    </div>
                  )}

                  {classItem.academic_year && (
                    <div className="detail-item">
                      <span><strong>{t('studentClasses.academicYear')}</strong> {classItem.academic_year}</span>
                    </div>
                  )}

                  {classItem.semester && (
                    <div className="detail-item">
                      <span><strong>{t('studentClasses.semester')}</strong> {classItem.semester}</span>
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
};

export default StudentClasses;

